import cn from 'classnames'
import { PropsWithChildren, useEffect, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'

import { IAsyncDownloadState } from './model'
import { CircleLoader } from './ui/circleLoader'
import { CircleProgressBar } from './ui/circleProgressBar'

import styles from './AsyncContentWrapper.module.scss'

export interface IAsyncContentWrapperTexts {
  requesting: { title: string; description: string }
  processing: { title: string; description: string }
  downloading: { title: string; description: string }
  error: { title: string; description: string; tryAgain: string }
}

interface IAsyncContentWrapperProps extends PropsWithChildren {
  download: IAsyncDownloadState
  isBlurContent: boolean
  onRetry?: () => void
  classes?: { container: string }
  texts: IAsyncContentWrapperTexts
}

export const AsyncContentWrapper = ({
  download,
  isBlurContent,
  children,
  onRetry,
  classes,
  texts,
}: IAsyncContentWrapperProps) => {
  const { status } = download
  const isSuccess = useMemo(() => status === 'success', [status])
  const isRequesting = useMemo(() => status === 'requesting', [status])
  const isDownloading = useMemo(() => status === 'starting' || status === 'downloading', [status])
  const isProcessing = useMemo(() => status === 'processing', [status])
  const isError = useMemo(() => status === 'error', [status])

  const shouldHideScroll = useMemo(
    () => isRequesting || isDownloading || isProcessing || isError || isSuccess,
    [isRequesting, isDownloading, isProcessing, isError, isSuccess]
  )

  useEffect(() => {
    const previousOverflow = document.body.style.overflow

    document.body.style.overflow = shouldHideScroll ? 'hidden' : previousOverflow

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [shouldHideScroll])

  if (isRequesting) {
    return (
      <div className={cn(styles.loaderContainer, classes?.container)}>
        <CircleLoader />
        <div className={styles.container}>
          <div className={styles.requestingText}>
            <div className={styles.requestingTitle}>
              <Translation text={texts.requesting.title} />
            </div>
            <div className={styles.requestingSubtitle}>
              <Translation text={texts.requesting.description} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={cn(styles.loaderContainer, classes?.container)}>
        <div className={styles.errorIcon}>
          <i className="kbase kbase-close-circle-alt" />
        </div>
        <div className={styles.container}>
          <div className={styles.requestingText}>
            <div className={styles.requestingTitle}>
              <Translation text={texts.error.title} />
            </div>
            <div className={styles.requestingSubtitle}>
              <Translation span text={texts.error.description} />
              <span>&nbsp;</span>
              <Button onClick={onRetry} variant="text" className={styles.reloadButton}>
                <Translation span text={texts.error.tryAgain} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isProcessing || isSuccess) {
    return (
      <div className={cn(styles.loaderContainer, classes?.container)}>
        <CircleLoader />
        <div className={styles.container}>
          <div className={styles.requestingText}>
            <div className={styles.requestingTitle}>
              <Translation text={texts.processing.title} />
            </div>
            <div className={styles.requestingSubtitle}>
              <Translation text={texts.processing.description} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isDownloading) {
    return (
      <div className={cn(styles.loaderContainer, classes?.container)}>
        <CircleProgressBar download={download} />
        <div className={styles.container}>
          <div className={styles.requestingText}>
            <div className={styles.requestingTitle}>
              <Translation text={texts.downloading.title} />
            </div>
            <div className={styles.requestingSubtitle}>
              <Translation text={texts.downloading.description} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(styles.root, {
        [styles.blurred]: isBlurContent,
      })}
    >
      {children}
    </div>
  )
}
